<template>
  <div class="orderRenewal-originOrderInfo">
    <QS-scroller-box>
      <!-- eslint-disable vue/no-mutating-props -->
      <OrderSearch
        ref="orderSearch"
        :orderType.sync="orderType"
        :orderSearchConfig.sync="orderSearchConfig"
      />
      <!-- eslint-enable vue/no-mutating-props -->
      <p class="tips" v-show="!orderSearchConfig.model.orderId">
        {{ orderSearchTip }}
      </p>
      <div v-if="orderSearchConfig.model.orderId">
        <template v-if="orderInfo">
          <QS-h4-title :title="orderInfo.title" />
          <form-text-box
            ref="orderInfo"
            :column="3"
            :dataList="orderInfo.list"
            :model="orderInfo.model"
          />
        </template>

        <template v-if="entRentOrderInfo">
          <QS-h4-title :title="entRentOrderInfo.title" />
          <form-text-box
            ref="entRentOrderInfo"
            :column="3"
            :dataList="entRentOrderInfo.list"
            :model="entRentOrderInfo.model"
          />
        </template>

        <template v-if="carInfo">
          <QS-h4-title title="车辆信息" />
          <form-text-box
            ref="carInfo"
            :column="3"
            :dataList="carInfo.list"
            :model="carInfo.model"
          />
        </template>

        <template v-if="customerInfo">
          <QS-h4-title title="客户信息" />
          <form-text-box
            ref="customerInfo"
            :column="3"
            :dataList="customerInfo.list"
            :model="customerInfo.model"
          />
        </template>
      </div>

      <template slot="bottomAction">
        <div class="btn-group">
          <el-button @click="handleCancel">取消</el-button>
          <el-button
            type="primary"
            :disabled="!hasOrderDetailInfo"
            @click="handleNext"
            >下一步</el-button
          >
        </div>
      </template>
    </QS-scroller-box>
  </div>
</template>

<script>
import { FuzzySearch as gerCarList } from '@/api/domain/cars'
import { searchByCustomerName } from '@/api/domain/customers'
import {
  getLeaseOrderDetail,
  getLeaseOrderIds,
  getRentOrderDetail,
  getRentOrderIds,
} from '@/api/domain/underLease'
import {
  ENT_RENT_ORDER_STATES,
  ORDER_STATES,
  ORDER_TYPES,
} from '@/pages/orders/orderRenewal/config'
import { debounce } from '@/utils'
import OrderSearch from '../../components/originOrderInfo/components/orderSearch/index.vue'
import { CUSTOMER_TYPES } from '../../config/originOrderInfo'
import {
  backfillEnterpriseOrderInfo,
  backfillEnterpriseSubEntRentOrderInfo,
  backfillEnterpriseSubOrderInfo,
  backfillPersonalOrderInfo,
  getOrderInfoMixinByOrderType,
} from '../../mixins/originOrderInfo.js'

export default {
  name: 'OriginOrderInfo',
  props: ['orderType'],
  data() {
    return {
      ...getOrderInfoMixinByOrderType(this.orderType).data.apply(this),
      carList: [],
      customerList: [],
      orderDataList: [],
      hasOrderDetailInfo: false,
    }
  },
  computed: {
    searchFormRef() {
      return this.$refs.orderSearch.$refs.searchForm
    },
    orderInfoRef() {
      return this.$refs.orderInfo
    },
    // eslint-disable-next-line vue/no-unused-properties
    entRentOrderInfoRef() {
      return this.$refs.entRentOrderInfo
    },
    carInfoRef() {
      return this.$refs.carInfo
    },
    customerInfoRef() {
      return this.$refs.customerInfo
    },
  },
  watch: {
    // 监听车牌号变化，并搜索订单ID
    'orderSearchConfig.model.carId': {
      handler: debounce(function (val) {
        if (val) {
          if (this.orderType === 'enterprise') {
            this.getLeaseOrderId()
          } else {
            this.getRentOrderId()
          }
        } else {
          this.reset()
        }
      }, 300),
    },
    // 监听客户名称变化，并搜索订单ID
    'orderSearchConfig.model.customerId': {
      handler: debounce(function (val) {
        // 根据客户信息回填deptName
        const customerDetail =
          this.customerList.find((item) => item.id === val) || {}
        if (customerDetail) {
          const { deptName } = customerDetail
          this.orderSearchConfig.model = {
            ...this.orderSearchConfig.model,
            deptName: deptName || '',
          }
        }

        if (val) {
          if (this.orderType === 'enterprise') {
            this.getLeaseOrderId()
          } else {
            this.getRentOrderId()
          }
        } else {
          this.reset()
        }
      }, 300),
    },
    // 监听订单ID变化，获取订单信息
    'orderSearchConfig.model.orderId': {
      handler: debounce(async function (val) {
        if (!val) {
          this.resetOtherFields()
          this.hasOrderDetailInfo = false
          return
        }
        this.handleOrderIdChange(this.orderType, val)
      }, 300),
    },
  },
  methods: {
    async handleOrderIdChange(orderType, orderId) {
      const action =
        orderType === 'enterprise'
          ? this.getLeaseOrderDetail
          : this.getRentOrderDetail
      const orderData = await action(orderId)
      this.hasOrderDetailInfo = true
      if (orderData) {
        this.orderDataList = [orderData]
        await this.backfill(orderData)
      }
    },
    reset() {
      try {
        if (this.orderSearchType === 'carId') {
          this.resetCarIdSelectOptions()
        }
        this.resetOrderId()
        this.resetOrderIdSelectOptions()
        this.resetOrderDataList()
        this.resetSearchFields()
        this.resetOtherFields()
      } catch (err) {
        console.error(err)
      }
    },
    resetOrderId() {
      this.$set(this.orderSearchConfig.model, 'orderId', '')
    },
    resetOrderIdSelectOptions() {
      this.searchFormRef?.setItemData({
        elConfigKey: 'list',
        key: 'orderId',
        data: [],
      })
    },
    resetOrderDataList() {
      this.orderDataList = []
    },
    resetCarIdSelectOptions() {
      this.searchFormRef?.setItemData({
        elConfigKey: 'list',
        key: 'carId',
        data: [],
      })
    },
    resetSearchFields() {
      this.searchFormRef?.getRefs()?.resetFields()
    },
    resetOtherFields() {
      try {
        this.orderInfoRef?.getRefs()?.resetFields()
        this.entRentOrderInfo?.getRefs()?.resetFields()
        this.carInfoRef?.getRefs()?.resetFields()
        this.customerInfoRef?.getRefs()?.resetFields()
      } catch (err) {
        console.error(err)
      }
    },
    handleCancel() {
      this.$confirm(
        '取消后可在「订单续约」列表继续操作订单确认流程，点击确定返回列表',
        '提示',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning',
        },
      ).then(() => {
        this.$refreshBack(false)
      }).catch(() => null)
    },
    handleNext() {
      this.$emit('completeOrderSelect', this.orderDataList)
    },
    // 查询方式切换
    // eslint-disable-next-line vue/no-unused-properties
    handelOrderQueryTypeToggle() {
      this.reset()
      if (this.orderSearchType === 'carId') {
        this.orderSearchType = 'customerId'
        this.searchFormRef?.setItemData({
          elConfigKey: 'isShow',
          key: 'carId',
          data: false,
        })
        this.searchFormRef?.setItemData({
          elConfigKey: 'isShow',
          key: 'customerId',
          data: true,
        })
      } else {
        this.orderSearchType = 'carId'
        this.searchFormRef?.setItemData({
          elConfigKey: 'isShow',
          key: 'customerId',
          data: false,
        })
        this.searchFormRef?.setItemData({
          elConfigKey: 'isShow',
          key: 'carId',
          data: true,
        })
      }
    },
    // 获取车辆列表
    // eslint-disable-next-line vue/no-unused-properties
    async gerCarList(query) {
      query = query.trim()

      let carList

      if (query.length >= 4) {
        this.searchFormRef?.setItemData({
          elConfigKey: 'loading',
          key: 'carId',
          data: true,
        })
        try {
          const { code, data } = await gerCarList({
            plateNo: query,
            state: 1,
          })
          if (code === 1) {
            carList = data
          }
          this.searchFormRef?.setItemData({
            elConfigKey: 'loading',
            key: 'carId',
            data: false,
          })
        } catch (err) {
          carList = []
          this.searchFormRef?.setItemData({
            elConfigKey: 'loading',
            key: 'carId',
            data: false,
          })
        }
        if (!carList.length) {
          this.$message.warning('车牌号不存在')
          this.reset()
        }
      } else {
        carList = []
      }

      this.searchFormRef?.setItemData({
        elConfigKey: 'list',
        key: 'carId',
        data: carList,
      })
      this.carList = carList
    },
    // 获取客户列表
    // eslint-disable-next-line vue/no-unused-properties
    async getCustomerList(query) {
      query = query.trim()
      if (query.length >= 1) {
        this.getCustomerListFromServer(query)
      }
    },
    async getCustomerListFromServer(query) {
      let customerList
      this.searchFormRef?.setItemData({
        elConfigKey: 'loading',
        key: 'customerId',
        data: true,
      })
      try {
        const { code, data } = await searchByCustomerName({
          customerName: query,
          customerType:
            ['enterprise', 'enterpriseSub'].includes(this.orderType)
              ? CUSTOMER_TYPES.enterprise.value
              : CUSTOMER_TYPES.personal.value, // 客户类型，1: 个人 2: 企业
        })
        if (code === 1) {
          customerList = data
        }
        this.searchFormRef?.setItemData({
          elConfigKey: 'loading',
          key: 'customerId',
          data: false,
        })
      } catch (_) {
        customerList = []
        this.searchFormRef?.setItemData({
          elConfigKey: 'loading',
          key: 'customerId',
          data: false,
        })
      }
      if (!customerList.length) {
        this.$message.warning('客户名称不存在')
        this.reset()
      }
      this.searchFormRef?.setItemData({
        elConfigKey: 'list',
        key: 'customerId',
        data: customerList,
      })
      this.customerList = customerList
    },
    // 根据车牌号或客户姓名查询订单(个人、企业子订单)
    async getRentOrderId() {
      getRentOrderIds({
        carId: this.orderSearchConfig.model.carId || undefined,
        customerId: this.orderSearchConfig.model.customerId || undefined,
        orderBizType:
            this.orderType === 'personal'
              ? ORDER_TYPES.personal.value
              : ORDER_TYPES.enterprise.value, // 订单类型，10: 个人订单 20: 企业子订单
        orderStates: [ORDER_STATES.processing.value], // 订单状态，2500: 履约中
      }).then(({ code, data }) => {
        if (code === 1) {
          if (data && data.length) {
            const orderIdList = data.map((item) => ({
              label: item.id,
              value: item.id,
            }))
            this.searchFormRef?.setItemData({
              elConfigKey: 'list',
              key: 'orderId',
              data: orderIdList,
            })

            // 如果只有一个订单则自动填充
            if (data.length === 1) {
              this.orderSearchConfig.model.orderId = data[0].id
              this.orderSearchConfig.model.deptName = data[0].deptName
            } else if (!this.orderSearchConfig.model.orderId) {
              this.$message.warning('该客户有多个未续约订单，请按订单id搜索')
            }
          } else {
            if (this.orderSearchConfig.model.carId) {
              this.$message.warning('车牌号无履约中状态订单，无法续约')
            } else {
              this.$message.warning('客户无履约中状态的订单，无法续约')
            }

            this.searchFormRef?.setItemData({
              elConfigKey: 'list',
              key: 'orderId',
              data: [],
            })
            this.orderSearchConfig.model.orderId = ''
          }
        }
      })
    },
    // 根据车牌号或客户姓名查询订单(企业主订单)
    async getLeaseOrderId() {
      getLeaseOrderIds({
        customerId: this.orderSearchConfig.model.customerId || undefined,
        stateList: [ENT_RENT_ORDER_STATES.processing.value], // 订单状态，2: 进行中
      }).then(({ code, data }) => {
        if (code === 1) {
          if (data && data.length) {
            const orderIdList = data.map((item) => ({
              label: item.id,
              value: item.id,
            }))
            this.searchFormRef?.setItemData({
              elConfigKey: 'list',
              key: 'orderId',
              data: orderIdList,
            })

            // 如果只有一个订单则自动填充
            if (data.length === 1) {
              this.orderSearchConfig.model.orderId = data[0].id
              this.orderSearchConfig.model.deptName = data[0].deptName
            } else if (!this.orderSearchConfig.model.orderId) {
              this.$message.warning('该客户有多个未续约订单，请按订单id搜索')
            }
          } else {
            this.$message.warning('客户无履约中状态的订单，无法续约')

            this.searchFormRef?.setItemData({
              elConfigKey: 'list',
              key: 'orderId',
              data: [],
            })
            this.orderSearchConfig.model.orderId = ''
          }
        }
      })
    },
    // 获取订单详情
    async getRentOrderDetail(orderId) {
      let res

      try {
        res = await getRentOrderDetail({
          orderId,
        })
      } catch (err) {
        console.error(err)
      }

      if (!res) return

      const { code, data } = res

      if (code === 1 && data) {
        this.orderSearchConfig.model.deptName = data.deptName
        return data
      }
    },
    // 获取企业租赁主订单详情
    async getLeaseOrderDetail(orderId) {
      let res

      try {
        res = await getLeaseOrderDetail({
          id: orderId,
          checkAccess: false,
        })
      } catch (err) {
        console.error(err)
      }

      if (!res) return

      const { code, data } = res
      if (code === 1 && data) {
        return data.orderInfo
      }
    },
    // 回填订单其他信息
    async backfill(data) {
      if (this.orderType === 'personal') {
        backfillPersonalOrderInfo.call(this, data)
      } else if (this.orderType === 'enterpriseSub') {
        backfillEnterpriseSubOrderInfo.call(this, data)
        // 企业子订单需要获取主订单信息
        const entRentOrderData = await this.getLeaseOrderDetail(
          data.entRentOrderId,
        )
        backfillEnterpriseSubEntRentOrderInfo.call(this, entRentOrderData)
        this.autoJumpToSecond()
      } else if (this.orderType === 'enterprise') {
        data.contractAttachment = this.getEchoUploadFileData(
          data.contractAttachment,
          true,
        )
        backfillEnterpriseOrderInfo.call(this, data)
        this.autoJumpToSecond()
      }
    },

    // 自动跳转到第二
    autoJumpToSecond() {
      if (this.$route.query.stepActive) {
        this.$nextTick(() => {
          this.handleNext()
        })
      }
    },
    // eslint-disable-next-line vue/no-unused-properties
    carIdChange(carId) {
      this.orderSearchConfig.model.carId = carId
    },
  },
  components: {
    OrderSearch,
  },
  mounted() {
    const {
      orderId = null,
      plateNo,
      carId,
      customerId,
      customerName,
    } = this.$route.query
    if (!orderId) {
      // 客户名称点击下拉列表就显示数据
      this.getCustomerListFromServer()
      return
    }
    this.orderSearchConfig.model.orderId = orderId
    if (customerName) {
      this.orderSearchConfig.model.customerId = customerId
      this.orderSearchConfig.model.customerName = customerName
      this.getCustomerList(customerName)
    } else {
      // 客户名称点击下拉列表就显示数据
      this.getCustomerListFromServer()
    }
    if (plateNo) {
      this.orderSearchConfig.model.plateNo = plateNo
      this.orderSearchConfig.model.carId = carId
    }
  },
}
</script>

<style lang="scss" scoped>
::v-deep .el-input--prefix .el-input__inner {
  padding-left: 12px !important;
}
::v-deep .qs-scrollerBox {
  min-height: 400px;
}
.tips {
  margin-left: 125px;
}
</style>
